/**
 * 인벤토리. 원작처럼 탭(장비/소비/설치/기타/캐시)별로 슬롯을 따로 가진다.
 */

import Phaser from 'phaser';
import { DROPS } from '../data/balance';
import { ITEMS, TAB_ORDER, type ItemTab } from '../data/items';
import { EV } from '../game/events';
import { isGearItem, rollGear, type GearPiece } from './gear';

export interface Slot {
  itemId: string;
  count: number;
  gear?: GearPiece;
}

export type SlotList = (Slot | null)[];

export interface SavedSlot {
  itemId: string;
  count: number;
}

export class Inventory {
  readonly events = new Phaser.Events.EventEmitter();
  private readonly tabs: Record<ItemTab, SlotList>;

  constructor() {
    this.tabs = TAB_ORDER.reduce(
      (acc, tab) => {
        acc[tab] = Array.from({ length: DROPS.inventorySlots }, () => null);
        return acc;
      },
      {} as Record<ItemTab, SlotList>,
    );
  }

  slots(tab: ItemTab): SlotList {
    return this.tabs[tab];
  }

  countOf(itemId: string): number {
    const def = ITEMS[itemId];
    if (!def) return 0;
    return this.tabs[def.tab].reduce(
      (sum, slot) => (slot?.itemId === itemId ? sum + slot.count : sum),
      0,
    );
  }

  /** 넣지 못한 개수를 반환한다 (0 이면 전부 들어감) */
  add(itemId: string, count: number, gear?: GearPiece): number {
    const def = ITEMS[itemId];
    if (!def) return count;
    const list = this.tabs[def.tab];
    if (isGearItem(def)) {
      let left = count;
      for (let n = 0; n < count; n++) {
        const empty = list.findIndex((s) => s === null);
        if (empty < 0) break;
        const piece = n === 0 && gear ? { ...gear, itemId } : rollGear(itemId);
        if (!piece) break;
        list[empty] = { itemId, count: 1, gear: piece };
        left -= 1;
      }
      if (left < count) this.events.emit(EV.inventoryChanged, { tab: def.tab });
      return left;
    }
    let left = count;

    for (const slot of list) {
      if (left <= 0) break;
      if (slot?.itemId !== itemId) continue;
      const room = DROPS.maxStack - slot.count;
      if (room <= 0) continue;
      const moved = Math.min(room, left);
      slot.count += moved;
      left -= moved;
    }

    for (let i = 0; i < list.length && left > 0; i++) {
      if (list[i] !== null) continue;
      const moved = Math.min(DROPS.maxStack, left);
      list[i] = { itemId, count: moved };
      left -= moved;
    }

    if (left < count) this.events.emit(EV.inventoryChanged, { tab: def.tab });
    return left;
  }

  /** 아이템 ID로 개수를 뺀다. 모자라면 false */
  removeItem(itemId: string, count: number): boolean {
    if (this.countOf(itemId) < count) return false;
    let left = count;
    const def = ITEMS[itemId];
    if (!def) return false;
    const list = this.tabs[def.tab];
    for (let i = 0; i < list.length && left > 0; i++) {
      const slot = list[i];
      if (!slot || slot.itemId !== itemId) continue;
      const take = Math.min(slot.count, left);
      slot.count -= take;
      left -= take;
      if (slot.count <= 0) list[i] = null;
    }
    this.events.emit(EV.inventoryChanged, { tab: def.tab });
    return left === 0;
  }

  removeAt(tab: ItemTab, index: number, count = 1): boolean {
    const slot = this.tabs[tab][index];
    if (!slot || slot.count < count) return false;
    slot.count -= count;
    if (slot.count <= 0) this.tabs[tab][index] = null;
    this.events.emit(EV.inventoryChanged, { tab });
    return true;
  }

  /** 해당 아이템이 들어있는 첫 슬롯 인덱스 */
  findSlot(itemId: string): { tab: ItemTab; index: number } | null {
    const def = ITEMS[itemId];
    if (!def) return null;
    const index = this.tabs[def.tab].findIndex((s) => s?.itemId === itemId);
    return index < 0 ? null : { tab: def.tab, index };
  }

  /** 조건에 맞는 소비 아이템 중 회복량이 가장 작은 것을 찾는다 (포션 단축키용) */
  findConsumable(kind: 'hp' | 'mp'): { tab: ItemTab; index: number; itemId: string } | null {
    let best: { tab: ItemTab; index: number; itemId: string; value: number } | null = null;
    this.tabs.use.forEach((slot, index) => {
      if (!slot) return;
      const def = ITEMS[slot.itemId];
      const value = def?.use?.[kind] ?? 0;
      if (value <= 0) return;
      if (!best || value < best.value) {
        best = { tab: 'use', index, itemId: slot.itemId, value };
      }
    });
    return best;
  }

  takeAt(tab: ItemTab, index: number): Slot | null {
    const slot = this.tabs[tab][index];
    if (!slot) return null;
    this.tabs[tab][index] = null;
    this.events.emit(EV.inventoryChanged, { tab });
    return slot;
  }

  putAt(tab: ItemTab, index: number, slot: Slot): boolean {
    if (this.tabs[tab][index]) return false;
    this.tabs[tab][index] = slot;
    this.events.emit(EV.inventoryChanged, { tab });
    return true;
  }

  /** 빈 칸에 슬롯을 넣는다 */
  put(slot: Slot): boolean {
    const def = ITEMS[slot.itemId];
    if (!def) return false;
    const list = this.tabs[def.tab];
    const empty = list.findIndex((s) => s === null);
    if (empty < 0) return false;
    list[empty] = slot;
    this.events.emit(EV.inventoryChanged, { tab: def.tab });
    return true;
  }

  toJSON(): { tab: ItemTab; index: number; itemId: string; count: number; gear?: GearPiece }[] {
    const out: { tab: ItemTab; index: number; itemId: string; count: number; gear?: GearPiece }[] = [];
    for (const tab of TAB_ORDER) {
      this.tabs[tab].forEach((slot, index) => {
        if (slot) out.push({ tab, index, itemId: slot.itemId, count: slot.count, gear: slot.gear });
      });
    }
    return out;
  }

  load(entries: readonly { tab: ItemTab; index: number; itemId: string; count: number; gear?: GearPiece }[]): void {
    for (const tab of TAB_ORDER) {
      this.tabs[tab] = Array.from({ length: DROPS.inventorySlots }, () => null);
    }
    for (const e of entries) {
      if (!ITEMS[e.itemId]) continue;
      const list = this.tabs[e.tab];
      if (!list || e.index < 0 || e.index >= list.length) continue;
      list[e.index] = { itemId: e.itemId, count: e.count, gear: e.gear };
    }
    TAB_ORDER.forEach((tab) => this.events.emit(EV.inventoryChanged, { tab }));
  }
}
